import React, { useState, useEffect } from 'react';
import orderService from '../../db/order-service';
import subscriptionOrderGenerator from '../../services/subscription-order-generator';
import './AdminDeliveryDashboard.scss';

const AdminDeliveryDashboard = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [deferredOrders, setDeferredOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    delivered: 0,
    deferred: 0
  });
  
  // Defer/Compensate Modal State
  const [deferModalOpen, setDeferModalOpen] = useState(false);
  const [selectedOrderForDefer, setSelectedOrderForDefer] = useState(null);
  const [deferAction, setDeferAction] = useState('refund'); // 'refund' | 'compensate'
  const [compensationDate, setCompensationDate] = useState('');
  const [superUserPwd, setSuperUserPwd] = useState('');
  const [deferReason, setDeferReason] = useState('');

  // Offline Subscription Modal State (localhost only)
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [offlineModalOpen, setOfflineModalOpen] = useState(false);
  const [offlinePhone, setOfflinePhone] = useState('');
  const [offlineCustomer, setOfflineCustomer] = useState(null);
  const [offlineCustomerError, setOfflineCustomerError] = useState('');
  const [offlinePlanType, setOfflinePlanType] = useState('immunity_only');
  const [offlineAmount, setOfflineAmount] = useState(700);
  const [offlineNote, setOfflineNote] = useState('');
  const [offlineDeliveryOption, setOfflineDeliveryOption] = useState('pickup');
  const [offlineProducts, setOfflineProducts] = useState([]);
  const [offlineSchedule, setOfflineSchedule] = useState({});
  const [offlineProductList, setOfflineProductList] = useState([]);
  const [offlineDateModal, setOfflineDateModal] = useState(false);
  const [offlineSelectedProduct, setOfflineSelectedProduct] = useState(null);
  const [offlineProcessing, setOfflineProcessing] = useState(false);

  const OFFLINE_PLANS = [
    { planType: 'immunity_only', name: 'Immunity Booster', defaultAmount: 700, shotsPerMonth: 20, smoothiesPerMonth: 0 },
    { planType: 'smoothie_only', name: 'Smoothie Fuel', defaultAmount: 1200, shotsPerMonth: 0, smoothiesPerMonth: 20 },
    { planType: 'complete_wellness', name: 'Custom Wellness Bundle', defaultAmount: 2400, shotsPerMonth: 20, smoothiesPerMonth: 20 },
  ];

  const generateOfflineSchedule = () => {
    const schedule = {};
    const start = new Date();
    start.setDate(start.getDate() + 1);
    let count = 0;
    while (count < 20) {
      const day = start.getDay();
      if (day >= 1 && day <= 5) {
        schedule[start.toISOString().split('T')[0]] = { shot: null, smoothie: null, status: 'pending' };
        count++;
      }
      start.setDate(start.getDate() + 1);
    }
    return schedule;
  };

  const searchOfflineCustomer = async () => {
    setOfflineCustomerError('');
    setOfflineCustomer(null);
    if (!offlinePhone.trim()) return;
    try {
      const { getDB } = await import('../../db/direct-couchdb');
      const db = getDB();
      const result = await db.find({ selector: { type: 'customer', phone: offlinePhone.trim() } });
      if (result.docs.length === 0) {
        setOfflineCustomerError('No customer found with this phone number.');
      } else {
        setOfflineCustomer(result.docs[0]);
        const products = await (await import('../../db/product-service')).default.getAllProducts();
        setOfflineProductList(products.filter(p => {
          const cats = Array.isArray(p.category) ? p.category : (p.category ? [p.category] : []);
          return !cats.includes('premium') && (cats.includes('shots') || cats.includes('smoothies') || p.name.toLowerCase().includes('shot') || p.name.toLowerCase().includes('juice') || p.name.toLowerCase().includes('smoothie'));
        }));
        setOfflineSchedule(generateOfflineSchedule());
        setOfflineProducts([]);
      }
    } catch (err) {
      setOfflineCustomerError('Error searching customer.');
    }
  };

  const toggleOfflineProductDate = (dateKey, productId, type) => {
    const newSchedule = { ...offlineSchedule };
    if (newSchedule[dateKey][type] === productId) {
      newSchedule[dateKey][type] = null;
    } else {
      newSchedule[dateKey][type] = productId;
    }
    setOfflineSchedule(newSchedule);
    const counts = {};
    Object.values(newSchedule).forEach(day => {
      if (day.shot) counts[day.shot] = (counts[day.shot] || 0) + 1;
      if (day.smoothie) counts[day.smoothie] = (counts[day.smoothie] || 0) + 1;
    });
    setOfflineProducts(Object.entries(counts).map(([productId, quantity]) => ({ productId, quantity })));
  };

  const getOfflineProductCount = (productId) => {
    return Object.values(offlineSchedule).filter(d => d.shot === productId || d.smoothie === productId).length;
  };

  const createOfflineSubscription = async () => {
    if (!offlineCustomer) { alert('Please search and select a customer first.'); return; }
    if (offlineProducts.length === 0) { alert('Please select products and dates.'); return; }
    if (!offlineNote.trim()) { alert('Please add a payment note.'); return; }
    setOfflineProcessing(true);
    try {
      const subscriptionService = await import('../../services/subscription-service');
      const { getDB } = await import('../../db/direct-couchdb');
      const db = getDB();
      const plan = OFFLINE_PLANS.find(p => p.planType === offlinePlanType);
      const now = new Date().toISOString();
      const subId = `subscription_${offlineCustomer._id}_${Date.now()}`;

      const subscription = {
        _id: subId,
        type: 'user_subscription',
        customerId: offlineCustomer._id,
        planType: offlinePlanType,
        planName: plan.name,
        monthlyPrice: offlineAmount,
        selectedProducts: offlineProducts,
        deliverySchedule: offlineSchedule,
        deliveryOption: offlineDeliveryOption,
        status: 'active',
        paymentMethod: 'cash',
        paymentNote: offlineNote,
        startDate: now,
        nextBillingDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString(),
        deliveriesCompleted: 0,
        deliveriesRemaining: 20,
        createdAt: now
      };
      await db.put(subscription);

      const paymentRecord = {
        _id: `cash_payment_${offlineCustomer._id}_${Date.now()}`,
        type: 'subscription_payment',
        subscriptionId: subId,
        customerId: offlineCustomer._id,
        amount: offlineAmount,
        currency: 'INR',
        planType: offlinePlanType,
        paymentMethod: 'cash',
        status: 'paid',
        note: offlineNote,
        createdAt: now
      };
      await db.put(paymentRecord);

      alert(`✅ Subscription created for ${offlineCustomer.name}!\nPlan: ${plan.name}\nAmount: ₹${offlineAmount} (Cash)\nDelivery starts tomorrow.`);
      setOfflineModalOpen(false);
      setOfflinePhone('');
      setOfflineCustomer(null);
      setOfflineNote('');
      setOfflineProducts([]);
      setOfflineSchedule({});
      fetchDeliveryOrders();
    } catch (err) {
      console.error('Error creating offline subscription:', err);
      alert('Error creating subscription: ' + err.message);
    }
    setOfflineProcessing(false);
  };

  useEffect(() => {
    fetchDeliveryOrders();
  }, [selectedDate]);

  useEffect(() => {
    applyFilters();
  }, [deliveryOrders, countryFilter, pincodeFilter, areaFilter]);

  const applyFilters = () => {
    let filtered = [...deliveryOrders];
    
    // Filter by country
    if (countryFilter !== 'ALL') {
      filtered = filtered.filter(order => {
        let addressStr = '';
        
        // Handle address object or string
        if (typeof order.shippingAddress === 'object' && order.shippingAddress) {
          addressStr = [
            order.shippingAddress.addressLine1,
            order.shippingAddress.addressLine2,
            order.shippingAddress.city,
            order.shippingAddress.state,
            order.shippingAddress.pinCode
          ].filter(Boolean).join(' ').toLowerCase();
        } else {
          addressStr = String(order.shippingAddress || '').toLowerCase();
        }
        
        if (countryFilter === 'IN') return addressStr.includes('india') || addressStr.includes('hyderabad') || addressStr.includes('mumbai');
        if (countryFilter === 'US') return addressStr.includes('usa') || addressStr.includes('america');
        return true;
      });
    }
    
    // Filter by pincode
    if (pincodeFilter) {
      filtered = filtered.filter(order => {
        if (typeof order.shippingAddress === 'object' && order.shippingAddress) {
          return order.shippingAddress.pinCode && order.shippingAddress.pinCode.includes(pincodeFilter);
        }
        return String(order.shippingAddress || '').includes(pincodeFilter);
      });
    }
    
    // Filter by area
    if (areaFilter) {
      filtered = filtered.filter(order => {
        let addressStr = '';
        
        if (typeof order.shippingAddress === 'object' && order.shippingAddress) {
          addressStr = [
            order.shippingAddress.addressLine1,
            order.shippingAddress.addressLine2,
            order.shippingAddress.city
          ].filter(Boolean).join(' ').toLowerCase();
        } else {
          addressStr = String(order.shippingAddress || '').toLowerCase();
        }
        
        return addressStr.includes(areaFilter.toLowerCase());
      });
    }
    
    setFilteredOrders(filtered);
    
    // Update stats
    const total = filtered.length;
    const delivered = filtered.filter(order => order.deliveryStatus === 'delivered').length;
    const deferred = filtered.filter(order => order.status === 'deferred').length;
    const pending = total - delivered - deferred;
    setStats({ total, pending, delivered, deferred });
  };

  const fetchDeliveryOrders = async () => {
    setLoading(true);
    try {
      console.log('DEBUG: Selected date:', selectedDate);
      
      // Check for active subscriptions first
      const subscriptionService = await import('../../services/subscription-service');
      const allSubscriptions = await subscriptionService.default.getAllSubscriptions();
      console.log('DEBUG: All subscriptions:', allSubscriptions);
      const activeSubscriptions = allSubscriptions.filter(s => s.status === 'active');
      console.log('DEBUG: Active subscriptions:', activeSubscriptions);
      
      // Log delivery schedules to help debug
      if (activeSubscriptions.length > 0) {
        console.log('DEBUG: First subscription delivery schedule dates:', Object.keys(activeSubscriptions[0].deliverySchedule || {}));
      }
      
      if (activeSubscriptions.length === 0) {
        console.log('DEBUG: No active subscriptions found');
        alert('No active subscriptions found. Please create a subscription first.');
        setDeliveryOrders([]);
        setLoading(false);
        return;
      }
      
      // Auto-generate subscription orders for the selected date
      console.log('DEBUG: Generating orders for date:', selectedDate);
      const generatedOrders = await subscriptionOrderGenerator.generateOrdersForDate(new Date(selectedDate));
      console.log('DEBUG: Generated orders:', generatedOrders);
      
      const [allOrders, subscriptionOrders] = await Promise.all([
        orderService.getAllOrders(),
        subscriptionOrderGenerator.getSubscriptionOrdersForDate(selectedDate)
      ]);
      
      console.log('DEBUG: All orders from DB:', allOrders.length);
      console.log('DEBUG: Subscription orders for date:', subscriptionOrders);
      
      // Filter regular orders for selected delivery date
      const regularOrdersForDate = allOrders.filter(order => {
        console.log('DEBUG: Checking order:', order._id, 'deliveryDate:', order.deliveryDate, 'date:', order.date);
        if (!order.deliveryDate) return false;
        const orderDeliveryDate = new Date(order.deliveryDate).toISOString().split('T')[0];
        const matches = orderDeliveryDate === selectedDate;
        console.log('DEBUG: Order delivery date:', orderDeliveryDate, 'Selected:', selectedDate, 'Matches:', matches);
        return matches;
      });
      
      console.log('DEBUG: Regular orders for date:', regularOrdersForDate.length);
      console.log('DEBUG: Regular orders:', regularOrdersForDate);

      // Enrich subscription orders with customer details
      const enrichedSubscriptionOrders = await Promise.all(
        subscriptionOrders.map(async (order) => {
          try {
            const customerService = await import('../../db/customer-service');
            const subscriptionService = await import('../../services/subscription-service');
            const [customer, addresses, subscription] = await Promise.all([
              customerService.default.getCustomerById(order.customerId),
              customerService.default.getCustomerAddresses(order.customerId),
              subscriptionService.default.getSubscriptionById(order.subscriptionId).catch(() => null)
            ]);
            const defaultAddress = addresses.find(addr => addr.isDefault) || addresses[0];
            const deliveryOption = subscription?.deliveryOption || subscription?.deliveryType || 'pickup';
            const subscriptionEndDate = subscription?.deliverySchedule
              ? Object.keys(subscription.deliverySchedule).sort().pop()
              : null;

            return {
              ...order,
              customerName: customer.name || 'Subscription Customer',
              shippingAddress: defaultAddress || 'No address',
              deliveryOption,
              subscriptionEndDate,
              deliveryTimeSlot: '8:00 AM - 9:00 AM'
            };
          } catch (error) {
            console.error('Error enriching subscription order:', error);
            return {
              ...order,
              customerName: 'Subscription Customer',
              shippingAddress: 'No address',
              deliveryOption: 'pickup',
              subscriptionEndDate: null,
              deliveryTimeSlot: '8:00 AM - 9:00 AM'
            };
          }
        })
      );

      // Combine regular and enriched subscription orders
      const allOrdersForDate = [...regularOrdersForDate, ...enrichedSubscriptionOrders];

      console.log('DEBUG: All orders for date:', allOrdersForDate);

      // Separate deferred orders
      const deferred = allOrders.filter(order => order.status === 'deferred');
      setDeferredOrders(deferred);

      // Sort by time slot
      const timeSlotOrder = { 'Morning': 1, 'Afternoon': 2, 'Evening': 3, 'Night': 4, '8:00 AM - 9:00 AM': 1, '9:00 AM - 10:00 AM': 1, '5:00 PM - 6:00 PM': 3, '6:00 PM - 7:00 PM': 3 };
      const sortedOrders = allOrdersForDate.sort((a, b) => {
        return (timeSlotOrder[a.deliveryTimeSlot] || 5) - (timeSlotOrder[b.deliveryTimeSlot] || 5);
      });

      setDeliveryOrders(sortedOrders);
    } catch (error) {
      console.error('Error fetching delivery orders:', error);
      setDeliveryOrders([]);
      setDeferredOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const createTestOrder = async () => {
    try {
      const { localDB } = await import('../../db/pouchdb-config');
      
      const testOrder = {
        _id: 'order_test_' + Date.now(),
        type: 'order',
        customerId: 'customer_test_' + Date.now(),
        customerName: 'Test Customer',
        date: new Date().toISOString(),
        deliveryDate: selectedDate,
        deliveryTimeSlot: 'Morning',
        items: [
          { name: 'Organic Tomatoes', quantity: 2, price: 50 },
          { name: 'Fresh Spinach', quantity: 1, price: 30 }
        ],
        totalAmount: 130,
        shippingAddress: '123 Test Street, Hyderabad, 500019',
        status: 'confirmed',
        deliveryStatus: 'pending'
      };
      
      await localDB.put(testOrder);
      alert('Test order created successfully!');
      
      // Refresh the orders
      fetchDeliveryOrders();
    } catch (error) {
      console.error('Error creating test order:', error);
      alert('Error creating test order: ' + error.message);
    }
  };

  const revokeDelivery = async (orderId) => {
    const password = prompt('Enter super user password to revoke delivery:');
    try {
      const verifyRes = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/verify-superuser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ password })
      });
      if (!verifyRes.ok) { alert('Invalid password!'); return; }

      const order = await orderService.getOrderById(orderId);
      order.deliveryStatus = 'pending';
      delete order.deliveredAt;
      order.updatedAt = new Date().toISOString();
      await orderService.updateOrder(order);
      fetchDeliveryOrders();
      alert('Delivery revoked successfully!');
    } catch (error) {
      console.error('Error revoking delivery:', error);
      alert('Error revoking delivery. Please try again.');
    }
  };

  // Helper to get next weekday (Mon-Fri)
  const getNextWorkingDay = (dateStr) => {
    const date = new Date(dateStr);
    // Add 1 day initially
    date.setDate(date.getDate() + 1);
    
    // If it's Saturday (6) or Sunday (0), keep adding days
    while (date.getDay() === 0 || date.getDay() === 6) {
      date.setDate(date.getDate() + 1);
    }
    return date.toISOString().split('T')[0];
  };

  const handleDeferClick = async (order) => {
    setSelectedOrderForDefer(order);
    setDeferAction('refund');
    setSuperUserPwd('');
    setDeferReason('');

    // Calculate recommended reschedule date based on subscription's last date
    let recommendedDate = '';
    
    if (order.isSubscriptionOrder && order.subscriptionId) {
      try {
        const subscriptionService = await import('../../services/subscription-service');
        const subscription = await subscriptionService.default.getSubscriptionById(order.subscriptionId);
        
        if (subscription && subscription.deliverySchedule) {
          // Get all dates from the schedule and find the last one
          const scheduleDates = Object.keys(subscription.deliverySchedule).sort();
          const lastScheduleDate = scheduleDates[scheduleDates.length - 1];
          
          // Get next working day after the last schedule date
          recommendedDate = getNextWorkingDay(lastScheduleDate);
          console.log('Subscription last date:', lastScheduleDate);
          console.log('Recommended reschedule date:', recommendedDate);
        } else {
          // Fallback: use next day after current delivery date
          const baseDate = order.deliveryDate || selectedDate;
          recommendedDate = getNextWorkingDay(baseDate);
        }
      } catch (error) {
        console.error('Error fetching subscription for defer:', error);
        // Fallback: use next day after current delivery date
        const baseDate = order.deliveryDate || selectedDate;
        recommendedDate = getNextWorkingDay(baseDate);
      }
    } else {
      // For non-subscription orders, use next day after delivery date
      const baseDate = order.deliveryDate || selectedDate;
      recommendedDate = getNextWorkingDay(baseDate);
    }

    setCompensationDate(recommendedDate);
    setDeferModalOpen(true);
  };

  const confirmDeferOrder = async () => {
    if (!selectedOrderForDefer) return;
    
    if (superUserPwd !== 'superuser123') {
      // Verify via backend
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/verify-superuser`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ password: superUserPwd })
        });
        if (!response.ok) { alert('Invalid Super User Password'); return; }
      } catch { alert('Invalid Super User Password'); return; }
    }

    try {
      const order = await orderService.getOrderById(selectedOrderForDefer._id);
      
      if (deferAction === 'compensate') {
        // Compensate Logic (Reschedule) - Clone strategy to prevent regeneration
        
        // 1. Mark original order as Rescheduled/Deferred
        order.status = 'deferred';
        order.deliveryStatus = 'Rescheduled';
        order.deferredDate = new Date().toISOString();
        order.rescheduledTo = compensationDate;
        order.deferAction = 'compensate';
        order.deferReason = deferReason;
        
        await orderService.updateOrder(order);

        // Update subscription delivery schedule if this is a subscription order
        if (order.isSubscriptionOrder && order.subscriptionId) {
          try {
            const subscriptionService = await import('../../services/subscription-service');
            const subscription = await subscriptionService.default.getSubscriptionById(order.subscriptionId);
            
            if (subscription && subscription.deliverySchedule) {
              const deliveryDateKey = order.deliveryDate;
              
              // Mark old date as deferred
              if (subscription.deliverySchedule[deliveryDateKey]) {
                subscription.deliverySchedule[deliveryDateKey].status = 'deferred';
                subscription.deliverySchedule[deliveryDateKey].deferredTo = compensationDate;
                subscription.deliverySchedule[deliveryDateKey].deferredAt = new Date().toISOString();
              }
              
              // Add new date to schedule with the same product
              const oldSchedule = subscription.deliverySchedule[deliveryDateKey];
              subscription.deliverySchedule[compensationDate] = {
                status: 'pending',
                shot: oldSchedule?.shot || null,
                smoothie: oldSchedule?.smoothie || null,
                rescheduledFrom: deliveryDateKey
              };
              
              subscription.updatedAt = new Date().toISOString();
              await subscriptionService.default.updateSubscription(subscription);
              console.log('Subscription schedule updated successfully');
            }
          } catch (subError) {
            console.error('Error updating subscription schedule:', subError);
            alert('Warning: Subscription schedule update failed. Error: ' + subError.message);
          }
        }

        // 2. Create NEW order for the new date
        const { localDB } = await import('../../db/pouchdb-config');
        
        const newOrder = {
          ...order,
          _id: `order_comp_${Date.now()}`,
          _rev: undefined, // Clear rev to create new doc
          date: new Date().toISOString(),
          deliveryDate: compensationDate,
          status: 'pending',
          deliveryStatus: 'pending',
          isCompensated: true,
          originalOrderId: order._id,
          // Clear defer fields
          deferredDate: undefined,
          rescheduledTo: undefined,
          deferAction: undefined,
          deliveredAt: undefined,
          paymentStatus: order.paymentStatus || 'pending'
        };

        await localDB.put(newOrder);
        
        // Send notification (optional)
        try {
          const pushNotificationManager = await import('../../services/push-notification-service');
          pushNotificationManager.default.showNotification('📅 Order Rescheduled', {
            body: `Your order has been rescheduled to ${new Date(compensationDate).toLocaleDateString()}.`,
            data: { orderId: order._id, type: 'rescheduled' }
          });
        } catch (notifError) {
          console.error('Error sending notification:', notifError);
        }

        // Send reschedule email
        try {
          await fetch(`${process.env.REACT_APP_API_URL || ''}/api/delivery/notify-defer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({
              customerId: order.customerId,
              action: 'compensate',
              items: order.orderItems || order.items || [],
              reason: deferReason,
              deliveryDate: order.deliveryDate || selectedDate,
              newDate: compensationDate
            })
          });
        } catch (emailErr) {
          console.error('Error sending reschedule email:', emailErr);
        }

        alert(`Order rescheduled to ${new Date(compensationDate).toLocaleDateString()}`);

      } else {
        // Standard Defer Logic (Refund)
        order.status = 'deferred';
        order.deliveryStatus = 'Deferred - Refund in 5 business days';
        order.deferredDate = new Date().toISOString();
        order.deferReason = deferReason;
        
        // Update subscription delivery schedule if this is a subscription order
        if (order.isSubscriptionOrder && order.subscriptionId) {
          try {
            const subscriptionService = await import('../../services/subscription-service');
            const subscription = await subscriptionService.default.getSubscriptionById(order.subscriptionId);
            
            if (subscription && subscription.deliverySchedule) {
              const deliveryDateKey = order.deliveryDate;
              if (subscription.deliverySchedule[deliveryDateKey]) {
                subscription.deliverySchedule[deliveryDateKey].status = 'deferred';
                await subscriptionService.default.updateSubscription(subscription);
              }
            }
          } catch (subError) {
            console.error('Error updating subscription schedule:', subError);
          }
        }
        
        // Send notification
        try {
          const pushNotificationManager = await import('../../services/push-notification-service');
          pushNotificationManager.default.showNotification('⚠️ Order Deferred', {
            body: `Your order has been deferred. Refund will be processed in 5 business days.`,
            data: { orderId: order._id, type: 'deferred' }
          });
        } catch (notifError) {
          console.error('Error sending notification:', notifError);
        }
        // Send refund email
        try {
          await fetch(`${process.env.REACT_APP_API_URL || ''}/api/delivery/notify-defer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({
              customerId: order.customerId,
              action: 'refund',
              items: order.orderItems || order.items || [],
              reason: deferReason,
              deliveryDate: order.deliveryDate || selectedDate
            })
          });
        } catch (emailErr) {
          console.error('Error sending defer email:', emailErr);
        }

        alert('Order deferred successfully. Refund process initiated.');
      }

      order.updatedAt = new Date().toISOString();
      await orderService.updateOrder(order);
      
      setDeferModalOpen(false);
      fetchDeliveryOrders();
    } catch (error) {
      console.error('Error deferring order:', error);
      alert('Error updating order. Please try again.');
    }
  };

  const confirmDelivery = async (orderId) => {
    try {
      const order = await orderService.getOrderById(orderId);
      order.deliveryStatus = 'delivered';
      order.deliveredAt = new Date().toISOString();
      order.updatedAt = new Date().toISOString();
      
      await orderService.updateOrder(order);
      
      // Update subscription delivery schedule if this is a subscription order
      if (order.isSubscriptionOrder && order.subscriptionId) {
        try {
          const subscriptionService = await import('../../services/subscription-service');
          const subscription = await subscriptionService.default.getSubscriptionById(order.subscriptionId);
          
          if (subscription && subscription.deliverySchedule) {
            const deliveryDateKey = order.deliveryDate;
            if (subscription.deliverySchedule[deliveryDateKey]) {
              subscription.deliverySchedule[deliveryDateKey].status = 'delivered';
              await subscriptionService.default.updateSubscription(subscription);
            }
          }
        } catch (subError) {
          console.error('Error updating subscription schedule:', subError);
        }
      }
      
      // Send notification to customer
      try {
        const pushNotificationManager = await import('../../services/push-notification-service');
        pushNotificationManager.default.showNotification('📦 Order Delivered', {
          body: `Your order has been delivered successfully!`,
          data: { orderId: order._id, type: 'delivery' }
        });
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }
      
      // Refresh the list
      fetchDeliveryOrders();
      
      alert('Delivery confirmed successfully!');
    } catch (error) {
      console.error('Error confirming delivery:', error);
      alert('Error confirming delivery. Please try again.');
    }
  };

  const getTimeSlotColor = (timeSlot) => {
    const colors = {
      '8:00 AM - 9:00 AM': '#4CAF50',
      '9:00 AM - 10:00 AM': '#66BB6A',
      '5:00 PM - 6:00 PM': '#2196F3',
      '6:00 PM - 7:00 PM': '#42A5F5'
    };
    return colors[timeSlot] || '#757575';
  };

  const generateShortOrderId = (order) => {
    // Get last 3 digits of phone number (reversed for uniqueness)
    let phoneCode = 'XXX';
    if (order.shippingAddress && typeof order.shippingAddress === 'object' && order.shippingAddress.phone) {
      const phone = order.shippingAddress.phone.toString().replace(/\D/g, '');
      if (phone.length >= 3) {
        phoneCode = phone.slice(-3).split('').reverse().join('');
      }
    }
    
    const orderDate = new Date(order.date);
    const dateStr = orderDate.getDate().toString().padStart(2, '0') + 
                   (orderDate.getMonth() + 1).toString().padStart(2, '0');
    
    const timeSlotCode = {
      '8:00 AM - 9:00 AM': 'M1',
      '9:00 AM - 10:00 AM': 'M2',
      '5:00 PM - 6:00 PM': 'E1',
      '6:00 PM - 7:00 PM': 'E2'
    }[order.deliveryTimeSlot] || 'X';
    
    return `${phoneCode}${dateStr}${timeSlotCode}`;
  };

  const formatAddress = (address) => {
    if (!address) return 'No address provided';
    
    // If address is an object, format it properly
    if (typeof address === 'object') {
      const parts = [
        address.addressLine1,
        address.addressLine2,
        address.city,
        address.state,
        address.pinCode
      ].filter(Boolean);
      
      const formatted = parts.join(', ');
      return formatted.length > 50 ? formatted.substring(0, 50) + '...' : formatted;
    }
    
    // If address is a string
    const addressStr = String(address);
    return addressStr.length > 50 ? addressStr.substring(0, 50) + '...' : addressStr;
  };

  return (
    <div className="admin-delivery-dashboard">
      <div className="dashboard-header">
        <h1>Delivery Dashboard</h1>
        <div className="controls">
          <div className="date-selector">
            <label>Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            <button 
              onClick={fetchDeliveryOrders}
              style={{
                padding: '8px 16px',
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginLeft: '10px'
              }}
            >
              Get Orders
            </button>
            {isLocalhost && (
              <button
                onClick={() => setOfflineModalOpen(true)}
                style={{
                  padding: '8px 16px',
                  background: '#7b1fa2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  marginLeft: '10px'
                }}
              >
                💵 Offline Subscription
              </button>
            )}
          </div>
          <div className="filter-controls">
            <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
              <option value="ALL">All Countries</option>
              <option value="IN">India</option>
              <option value="US">USA</option>
            </select>
            <input
              type="text"
              placeholder="Pincode"
              value={pincodeFilter}
              onChange={(e) => setPincodeFilter(e.target.value)}
            />
            <input
              type="text"
              placeholder="Area"
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="delivery-stats">
        <div className="stat-card total">
          <h3>{stats.total}</h3>
          <p>Total Orders</p>
        </div>
        <div className="stat-card pending">
          <h3>{stats.pending}</h3>
          <p>Pending Delivery</p>
        </div>
        <div className="stat-card delivered">
          <h3>{stats.delivered}</h3>
          <p>Delivered</p>
        </div>
        <div className="stat-card deferred">
          <h3>{stats.deferred}</h3>
          <p>Deferred</p>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading delivery orders...</div>
      ) : (
        <div>
          {filteredOrders.length === 0 ? (
            <div className="no-orders">
              <p>No deliveries scheduled for {new Date(selectedDate).toLocaleDateString()}</p>
              <button 
                className="create-test-order-btn"
                onClick={createTestOrder}
                style={{
                  marginTop: '20px',
                  padding: '15px 30px',
                  backgroundColor: '#133c0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                Create Test Order for This Date
              </button>
            </div>
          ) : (
            <div className="delivery-orders">
              <h2>Orders for {new Date(selectedDate).toLocaleDateString()}</h2>
              <p>Total: {filteredOrders.length} orders</p>
              
              {/* Product Summary */}
              <div style={{
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>📦 Product Requirements Summary</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {(() => {
                    const productCounts = {};
                    filteredOrders.forEach(order => {
                      (order.items || order.orderItems || []).forEach(item => {
                        const name = item.name || item.productName;
                        productCounts[name] = (productCounts[name] || 0) + item.quantity;
                      });
                    });
                    return Object.entries(productCounts).map(([product, count]) => (
                      <div key={product} style={{
                        padding: '12px',
                        backgroundColor: 'white',
                        borderLeft: '4px solid #4CAF50',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>{product}</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50' }}>{count} units</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Orders Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#4CAF50', color: 'white' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Order ID</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Customer</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Contact</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Address</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Delivery Type</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Sub. End Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Time Slot</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Items</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Amount</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, idx) => (
                      <tr key={order._id} style={{
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white'
                      }}>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '600', color: '#2c3e50' }}>#{generateShortOrderId(order)}</div>
                          {order.isSubscriptionOrder && (
                            <span style={{
                              fontSize: '10px',
                              backgroundColor: '#2196F3',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontWeight: '600'
                            }}>SUB</span>
                          )}
                          {order.isCompensated && (
                            <div style={{ 
                              fontSize: '10px', 
                              color: '#e65100', 
                              marginTop: '2px',
                              fontWeight: 'bold'
                            }}>Rescheduled</div>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '500' }}>{order.customerName || 'Customer'}</div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {order.shippingAddress && typeof order.shippingAddress === 'object' && order.shippingAddress.phone ? (
                            <div style={{ fontSize: '14px' }}>📞 {order.shippingAddress.phone}</div>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', maxWidth: '200px' }}>
                          <div style={{ fontSize: '13px', color: '#666' }}>{formatAddress(order.shippingAddress)}</div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {order.deliveryOption === 'delivery' || order.deliveryType === 'delivery' ? (
                            <span style={{ fontSize: '12px', backgroundColor: '#1976d2', color: 'white', padding: '3px 8px', borderRadius: '4px', fontWeight: '600' }}>🏠 Home Delivery</span>
                          ) : (
                            <span style={{ fontSize: '12px', backgroundColor: '#388e3c', color: 'white', padding: '3px 8px', borderRadius: '4px', fontWeight: '600' }}>🏪 Store Pickup</span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {order.subscriptionEndDate ? (
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>
                                {new Date(order.subscriptionEndDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                              {new Date(order.subscriptionEndDate) < new Date(new Date().setDate(new Date().getDate() + 7)) && (
                                <div style={{ fontSize: '11px', color: '#e65100', fontWeight: '600', marginTop: '2px' }}>⚠ Ending soon</div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{order.deliveryTimeSlot || '-'}</div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontSize: '13px' }}>
                            {(order.items || order.orderItems || []).slice(0, 2).map((item, i) => (
                              <div key={i} style={{ marginBottom: '3px' }}>
                                {item.name || item.productName} x{item.quantity}
                              </div>
                            ))}
                            {(order.items || order.orderItems || []).length > 2 && (
                              <div style={{ color: '#666', fontSize: '12px' }}>+{(order.items || order.orderItems).length - 2} more</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <div style={{ fontWeight: '600', color: '#2c3e50' }}>₹{order.totalAmount}</div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {order.deliveryStatus === 'delivered' ? (
                            <span style={{
                              padding: '6px 12px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>✓ Delivered</span>
                          ) : order.status === 'deferred' ? (
                            <span style={{
                              padding: '6px 12px',
                              backgroundColor: '#ff9800',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>⚠ Deferred</span>
                          ) : selectedDate > new Date().toISOString().split('T')[0] ? (
                            <span style={{
                              padding: '6px 12px',
                              backgroundColor: '#2196F3',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>📅 Scheduled</span>
                          ) : selectedDate < new Date().toISOString().split('T')[0] ? (
                            <span style={{
                              padding: '6px 12px',
                              backgroundColor: '#f44336',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>⏰ Missed</span>
                          ) : (
                            <span style={{
                              padding: '6px 12px',
                              backgroundColor: '#ffc107',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>⏳ Pending</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {order.deliveryStatus !== 'delivered' && order.status !== 'deferred' && selectedDate === new Date().toISOString().split('T')[0] ? (
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                              <button 
                                onClick={() => confirmDelivery(order._id)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#4CAF50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                ✓ Confirm
                              </button>
                              <button 
                                onClick={() => handleDeferClick(order)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#ff9800',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                Defer
                              </button>
                            </div>
                          ) : order.deliveryStatus === 'delivered' ? (
                            <button 
                              onClick={() => revokeDelivery(order._id)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Revoke
                            </button>
                          ) : (
                            <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Deferred Orders Section */}
          {deferredOrders.filter(order => order.deliveryStatus !== 'Refund Complete').length > 0 && (
            <div className="deferred-orders-section">
              <h2 style={{ color: '#ff9800', marginTop: '40px' }}>Deferred Orders ({deferredOrders.filter(order => order.deliveryStatus !== 'Refund Complete').length})</h2>
              <div className="orders-grid">
                {deferredOrders.filter(order => order.deliveryStatus !== 'Refund Complete').map(order => (
                  <div key={order._id} className="order-card deferred">
                    <div className="order-header">
                      <span className="order-id">#{generateShortOrderId(order)}</span>
                      <span className="delivery-status deferred">
                        Deferred
                      </span>
                    </div>
                    
                    <div className="customer-info">
                      <h4>{order.customerName || 'Customer'}</h4>
                      <p className="address">{formatAddress(order.shippingAddress)}</p>
                    </div>
                    
                    <div className="order-items">
                      <h5>Items ({order.items?.length || 0}):</h5>
                      {order.items?.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="item">
                          {item.name} x {item.quantity}
                        </div>
                      ))}
                      {order.items?.length > 3 && (
                        <div className="more-items">+{order.items.length - 3} more items</div>
                      )}
                    </div>
                    
                    <div className="order-total">
                      <strong>Total: ₹{order.totalAmount}</strong>
                    </div>
                    
                    {order.deliveryStatus === 'Refund Complete' ? (
                      <div className="refund-complete-info" style={{
                        padding: '12px',
                        backgroundColor: '#d4edda',
                        color: '#155724',
                        borderRadius: '6px',
                        textAlign: 'center',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        ✓ Refund Completed<br/>
                        <small>Completed on: {new Date(order.refundCompletedDate).toLocaleDateString()}</small>
                      </div>
                    ) : (
                      <div className="deferred-info" style={{
                        padding: '12px',
                        backgroundColor: '#fff3cd',
                        color: '#856404',
                        borderRadius: '6px',
                        textAlign: 'center',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        ⚠️ Refund in 5 business days<br/>
                        <small>Deferred on: {new Date(order.deferredDate).toLocaleDateString()}</small>
                        <button
                          onClick={async () => {
                            if (window.confirm('Mark refund as complete?')) {
                              try {
                                const orderDoc = await orderService.getOrderById(order._id);
                                orderDoc.deliveryStatus = 'Refund Complete';
                                orderDoc.refundCompletedDate = new Date().toISOString();
                                await orderService.updateOrder(orderDoc);
                                alert('Refund marked as complete.');
                                fetchDeliveryOrders();
                              } catch (err) {
                                alert('Failed to update refund status.');
                              }
                            }
                          }}
                          style={{
                            marginTop: '10px',
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          Refund Complete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Defer/Compensate Modal */}
      {deferModalOpen && (
        <div className="modal-overlay" onClick={() => setDeferModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Manage Order #{selectedOrderForDefer ? generateShortOrderId(selectedOrderForDefer) : ''}</h3>
            
            <div className="modal-options">
              <label>
                <input 
                  type="radio" 
                  name="deferAction" 
                  value="refund" 
                  checked={deferAction === 'refund'} 
                  onChange={(e) => setDeferAction(e.target.value)} 
                />
                <strong>Standard Defer</strong> (Mark deferred & Refund)
              </label>
              
              <label>
                <input 
                  type="radio" 
                  name="deferAction" 
                  value="compensate" 
                  checked={deferAction === 'compensate'} 
                  onChange={(e) => setDeferAction(e.target.value)} 
                />
                <strong>Compensate</strong> (Reschedule to new date)
              </label>
            </div>

            {deferAction === 'compensate' && (
              <div className="compensate-section">
                <label>New Delivery Date (Weekday):</label>
                <input 
                  type="date" 
                  className="form-control"
                  value={compensationDate} 
                  onChange={(e) => setCompensationDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}

            <div className="password-section">
              <label>Reason for Deferral: *</label>
              <textarea
                value={deferReason}
                onChange={(e) => setDeferReason(e.target.value)}
                placeholder="e.g. On the occasion of Good Friday, your delivery has been deferred"
                rows={3}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', resize: 'vertical' }}
              />
            </div>

            <div className="password-section">
              <label>Super User Password:</label>
              <input
                type="password"
                value={superUserPwd}
                onChange={(e) => setSuperUserPwd(e.target.value)}
                placeholder="Enter password"
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setDeferModalOpen(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeferOrder}
                disabled={!superUserPwd || !deferReason.trim()}
                className={`confirm-btn ${!superUserPwd || !deferReason.trim() ? 'disabled' : ''}`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Offline Subscription Modal - localhost only */}
      {isLocalhost && offlineModalOpen && (
        <div className="modal-overlay" onClick={() => setOfflineModalOpen(false)}>
          <div className="modal-content offline-modal" onClick={e => e.stopPropagation()}>
            <h3>💵 Create Offline / Cash Subscription</h3>

            {/* Customer Search */}
            <div className="password-section">
              <label>Customer Phone Number: *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={offlinePhone}
                  onChange={e => setOfflinePhone(e.target.value)}
                  placeholder="e.g. 9505757672"
                  onKeyDown={e => e.key === 'Enter' && searchOfflineCustomer()}
                />
                <button onClick={searchOfflineCustomer} style={{ padding: '8px 14px', background: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>🔍 Find</button>
              </div>
              {offlineCustomerError && <div style={{ color: '#f44336', fontSize: '13px', marginTop: '6px' }}>{offlineCustomerError}</div>}
              {offlineCustomer && (
                <div style={{ marginTop: '8px', padding: '10px', background: '#e8f5e9', borderRadius: '6px', fontSize: '13px' }}>
                  <strong>✅ {offlineCustomer.name}</strong> &bull; {offlineCustomer.phone} &bull; {offlineCustomer.email}
                </div>
              )}
            </div>

            {/* Plan Selection */}
            <div className="password-section">
              <label>Subscription Plan: *</label>
              <select
                value={offlinePlanType}
                onChange={e => {
                  setOfflinePlanType(e.target.value);
                  const plan = OFFLINE_PLANS.find(p => p.planType === e.target.value);
                  setOfflineAmount(plan.defaultAmount);
                  setOfflineSchedule(generateOfflineSchedule());
                  setOfflineProducts([]);
                }}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                {OFFLINE_PLANS.map(p => <option key={p.planType} value={p.planType}>{p.name} — ₹{p.defaultAmount}</option>)}
              </select>
            </div>

            {/* Amount */}
            <div className="password-section">
              <label>Amount Received (&#8377;): *</label>
              <input
                type="number"
                value={offlineAmount}
                onChange={e => setOfflineAmount(parseInt(e.target.value) || 0)}
              />
            </div>

            {/* Delivery Option */}
            <div className="password-section">
              <label>Delivery Option: *</label>
              <div style={{ display: 'flex', gap: '20px', marginTop: '6px' }}>
                <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                  <input type="radio" name="offlineDelivery" value="pickup" checked={offlineDeliveryOption === 'pickup'} onChange={() => setOfflineDeliveryOption('pickup')} />
                  {' '}🏪 Store Pickup
                </label>
                <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                  <input type="radio" name="offlineDelivery" value="delivery" checked={offlineDeliveryOption === 'delivery'} onChange={() => setOfflineDeliveryOption('delivery')} />
                  {' '}🏠 Home Delivery
                </label>
              </div>
            </div>

            {/* Payment Note */}
            <div className="password-section">
              <label>Payment Note: *</label>
              <input
                type="text"
                value={offlineNote}
                onChange={e => setOfflineNote(e.target.value)}
                placeholder={`e.g. Cash received on ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}
              />
            </div>

            {/* Product Selection */}
            {offlineCustomer && offlineProductList.length > 0 && (
              <div className="password-section">
                <label>Select Products &amp; Dates: *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', marginTop: '8px' }}>
                  {offlineProductList.map(product => (
                    <div
                      key={product._id}
                      onClick={() => { setOfflineSelectedProduct(product); setOfflineDateModal(true); }}
                      style={{ padding: '10px', border: `2px solid ${getOfflineProductCount(product._id) > 0 ? '#4CAF50' : '#ddd'}`, borderRadius: '8px', cursor: 'pointer', textAlign: 'center', fontSize: '12px', background: getOfflineProductCount(product._id) > 0 ? '#e8f5e9' : 'white' }}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>{product.name}</div>
                      <div style={{ color: '#4CAF50', fontWeight: '700' }}>{getOfflineProductCount(product._id)} days</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button onClick={() => setOfflineModalOpen(false)} className="cancel-btn">Cancel</button>
              <button
                onClick={createOfflineSubscription}
                disabled={offlineProcessing || !offlineCustomer || offlineProducts.length === 0 || !offlineNote.trim()}
                className={`confirm-btn ${offlineProcessing || !offlineCustomer || offlineProducts.length === 0 || !offlineNote.trim() ? 'disabled' : ''}`}
                style={{ background: '#7b1fa2' }}
              >
                {offlineProcessing ? 'Creating...' : '💵 Create Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline Date Picker Modal */}
      {isLocalhost && offlineDateModal && offlineSelectedProduct && (
        <div className="modal-overlay" onClick={() => setOfflineDateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Select Dates — {offlineSelectedProduct.name}</h3>
              <button onClick={() => setOfflineDateModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', maxHeight: '400px', overflowY: 'auto' }}>
              {Object.entries(offlineSchedule).map(([dateKey, day]) => {
                const type = offlineSelectedProduct.name.toLowerCase().includes('smoothie') ? 'smoothie' : 'shot';
                const isSelected = day[type] === offlineSelectedProduct._id;
                const isOther = day[type] && day[type] !== offlineSelectedProduct._id;
                return (
                  <div
                    key={dateKey}
                    onClick={() => !isOther && toggleOfflineProductDate(dateKey, offlineSelectedProduct._id, type)}
                    style={{ padding: '10px', border: `2px solid ${isSelected ? '#4CAF50' : isOther ? '#eee' : '#ddd'}`, borderRadius: '8px', cursor: isOther ? 'not-allowed' : 'pointer', textAlign: 'center', background: isSelected ? '#e8f5e9' : isOther ? '#f5f5f5' : 'white', opacity: isOther ? 0.5 : 1 }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: '600' }}>{new Date(dateKey).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                    {isSelected && <div style={{ color: '#4CAF50', fontWeight: '700', fontSize: '16px' }}>✓</div>}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button onClick={() => setOfflineDateModal(false)} style={{ padding: '10px 24px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeliveryDashboard;